import { useMemo } from 'react';
import { isFunction } from '../utils.js';
import { useDerivedStateFromProps } from './useDerivedStateFromProps.js';

type ItemT = Record<string, string>;
interface UseExpandStateArg {
  items?: ItemT[];
  itemKey?: keyof ItemT;
  expanded?: Record<string, boolean>;
  onExpand: (item: ItemT) => void;
  onExpandChange: (nextState: Record<string, boolean>, item?: ItemT) => void;
  onExpandAll: (nextState: Record<string, boolean>) => void;
  onToggleExpand: (item: ItemT) => void;
  onCollapse: (item: ItemT) => void;
}

const toggleStateInObject = <T,>(obj: Record<string, T>, key: string, value: T) => {
  const nextObj = { ...obj };
  if (nextObj[key]) {
    delete nextObj[key];
  } else {
    nextObj[key] = value;
  }
  return nextObj;
};

const noop = () => null;

export const useExpandState = ({
  items = [],
  itemKey = 'id',
  expanded: expandedProp = {},
  onExpand,
  onExpandChange = noop,
  onExpandAll = noop,
  onToggleExpand,
  onCollapse,
}: UseExpandStateArg) => {
  const [expanded, setExpanded] = useDerivedStateFromProps(expandedProp);

  const isExpandedAll = useMemo(() => Object.keys(expanded).length === items.length, [expanded, items]);

  const toggleExpandAll = (stateData = true) => {
    setExpanded(() => {
      const nextState: Record<string, boolean> = {};
      if (!isExpandedAll) {
        items.forEach((item) => {
          nextState[item[itemKey]] = stateData;
        });
      }
      onExpandChange(nextState);
      onExpandAll(nextState);
      return nextState;
    });
  };

  const expand = (item: ItemT, stateData = true) => {
    if (!item) return;
    if (isFunction(onExpand)) {
      onExpand(item);
    }
    setExpanded((prevState) => {
      const key = item[itemKey];
      const nextState = { ...prevState, [key]: stateData };
      onExpandChange(nextState, item);
      return nextState;
    });
  };

  const toggleExpand = (item: ItemT, stateData = true) => {
    if (isFunction(onToggleExpand)) {
      onToggleExpand(item);
    }
    setExpanded((prevState) => {
      const key = item[itemKey];
      const nextState = toggleStateInObject(prevState, key, stateData);
      onExpandChange(nextState, item);
      return nextState;
    });
  };

  const collapse = (item: ItemT) => {
    if (isFunction(onCollapse)) {
      onCollapse(item);
    }
    setExpanded((prevState) => {
      const nextState = { ...prevState };
      delete nextState[item[itemKey]];
      onExpandChange(nextState, item);
      return nextState;
    });
  };

  return {
    state: { expanded, isExpandedAll },
    actions: {
      expand,
      toggleExpandAll,
      toggleExpand,
      collapse,
    },
    setExpanded,
  };
};

export default useExpandState;
